import { useEffect, useState } from 'react'
import CustomPaper from '../../containers/Paper'
import { UserCredential, UserDTO } from '../../models/users'
import UserService from '../../services/UserService'
import { dateFormatter, formatDateToOnlyMonthAndYear, isAdmin } from '../../helpers/utils'
import { Navigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import { AppConfig } from '../../models/shared'
import UtilitiesService from '../../services/UtilitiesService'
import { CircularProgress } from '@mui/material'
import html2canvas from 'html2canvas'
import CustomButton from '../../components/Button'
import DownloadIcon from '@mui/icons-material/Download'
import downloadjs from 'downloadjs'

const DEFAULT_CREDENTIAL = {
    career: '',
    dayOfGraduated: '',
    documentType: '',
    documentValue: '',
    firstName: '',
    image: '',
    lastName: '',
    qrCodeImage: '',
    token: '',
    university: '',
}

const CredentialPage = () => {
    const [user, setUser] = useState<UserDTO>()
    const [userCredential, setUserCredential] = useState<UserCredential>()
    const [config, setConfig] = useState<AppConfig>()
    const [image, setImage] = useState<Blob>()
    const [downloading, setDownloading] = useState<boolean>(false)

    useEffect(() => {
        new UserService().getCurrentUser().then((response) => {
            if (response.status !== 200) return
            setUser(response.data)
        })
        new UtilitiesService().getAppConfig().then((response) => {
            if (response.status !== 200) return
            setConfig(response.data)
        })
    }, [])

    useEffect(() => {
        if (!user || !config) return
        new UserService().getCredential(user.id, config.university).then((response) => {
            if (response.status !== 200) setUserCredential(DEFAULT_CREDENTIAL)
            else {
                const imageUrl = !!response?.data?.image ? response.data.image : '/adminPhoto.png'
                fetch(imageUrl)
                    .then((res) => res.blob())
                    .then((imageBlob) => setImage(imageBlob))
                setUserCredential(response.data)
            }
        })
    }, [user, config])

    const handleDownload = async () => {
        setDownloading(true)
        setTimeout(async () => {
            const credential = document.getElementById('credencial')
            if (!credential) return

            const canvas = await html2canvas(credential)
            const dataURL = canvas.toDataURL('image/png')
            downloadjs(dataURL, 'credencial_de_graduado.png', 'image/png')
            setDownloading(false)
        }, 100)
    }

    return (
        <>
            {!isAdmin() ? (
                <div className="w-full relative h-[calc(100vh-132px)] flex justify-center items-center text-secondary">
                    {userCredential && user && config ? (
                        userCredential.token !== '' ? (
                            <>
                                <div className="flex gap-5 absolute top-0 bottom-0 w-full h-fit text-start items-center">
                                    <span className="text-2xl md:text-4xl font-semibold">
                                        Credencial de graduado
                                    </span>
                                    <CustomButton
                                        secondary
                                        onClick={handleDownload}
                                        className="h-fit w-fit"
                                        startIcon={<DownloadIcon />}
                                    >
                                        Descargar
                                    </CustomButton>
                                </div>
                                <div
                                    className="bg-light mt-0 md:mt-10 md:p-10"
                                    id="credencial"
                                >
                                    <div
                                        className={`min-w-[350px] md:min-w-[550px] md:max-w-[50vw] h-fit p-0 overflow-hidden bg-white rounded-default ${downloading ? 'border border-1 border-solid border-disabled' : 'shadow'}`}
                                    >
                                        <div className="bg-primary h-4 md:h-8 w-full" />
                                        <div className="flex flex-col p-4 md:p-8 h-full">
                                            <div className="flex justify-between">
                                                <div className="flex flex-col gap-8 h-full">
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex gap-3 md:gap-6 items-center">
                                                            <img
                                                                src={
                                                                    !!image
                                                                        ? URL.createObjectURL(image)
                                                                        : '/adminPhoto.png'
                                                                }
                                                                alt="user"
                                                                className="h-16 md:h-32 rounded-[100%] outline outline-1 outline-secondary"
                                                            />
                                                            <div className="flex flex-col justify-center gap-1 md:gap-2">
                                                                <span className="text-xl md:text-3xl font-bold">{`${userCredential.firstName} ${userCredential.lastName}`}</span>
                                                                <span className="text-base md:text-xl">{`${userCredential.documentType} ${userCredential.documentValue}`}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex flex-col gap-2 md:gap-3 text-base md:text-xl mt-2 md:mt-0">
                                                            <span className="text-xs md:text-base">
                                                                <b>Institución: </b>
                                                                {userCredential.university}
                                                            </span>
                                                            <span className="text-xs md:text-base">
                                                                <b>Carrera: </b>
                                                                {userCredential.career}
                                                            </span>
                                                            <span className="text-xs md:text-base">
                                                                <b>Fecha de graduación: </b>
                                                                {formatDateToOnlyMonthAndYear(
                                                                    dateFormatter(
                                                                        new Date(
                                                                            userCredential.dayOfGraduated,
                                                                        ),
                                                                    ),
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-center gap-4 h-full">
                                                    <div className="flex justify-between items-center pt-5">
                                                        {!!config?.logo ? (
                                                            <img
                                                                src={config.logo}
                                                                alt="logo"
                                                                className="h-10 md:h-20"
                                                            />
                                                        ) : (
                                                            <div className="flex gap-2 md:gap-4 items-center">
                                                                <img
                                                                    src="/logo.png"
                                                                    alt="logo"
                                                                    className="h-8 md:h-16"
                                                                />
                                                                <span className="text-3xl md:text-5xl font-bold">
                                                                    SiSeG
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <img
                                                            src={`data:image/png;base64,${userCredential.qrCodeImage}`}
                                                            alt="qr"
                                                            className="h-24 md:h-44"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1 mt-2 md:mt-3">
                                                <span className="text-[10px] md:text-sm">
                                                    <b>Código de validación: </b>
                                                    {userCredential.token}
                                                </span>
                                                <span className="text-[10px] md:text-sm">
                                                    {`Esta credencial puede ser validada ingresando a ${process.env.REACT_APP_BASE_URL}${AppRoutes.validateCredential} y completando el código de validación, o bien escaneando el QR, que ya tiene al código precargado.`}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="bg-primary h-4 md:h-8 w-full" />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <CustomPaper className="w-[75vw] md:w-[50vw] flex flex-col gap-2 md:gap-4">
                                <span className="font-bold text-lg md:text-2xl">
                                    Hubo un error al obtener la credencial.
                                </span>
                                <span className="text-base md:text-xl">
                                    Probablemente se deba a que no se encontró entre tu historial
                                    educativo a la graduación de la institución:{' '}
                                    <b>{config.university}</b>
                                </span>
                                <span className="text-sm md:text-lg">
                                    En caso de tener tu graduación correctamente cargada, contactá a
                                    un administrador.
                                </span>
                            </CustomPaper>
                        )
                    ) : (
                        <div className="flex w-full justify-center">
                            <CircularProgress
                                className="m-2 md:m-5 text-primary"
                                size={64}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <Navigate to={AppRoutes.login} />
            )}
        </>
    )
}

export default CredentialPage
